import { ConditionFact, PageType } from "@reconvert/reconvert-utils";
import { APP_PLATFORM } from "../config";
import { Platform } from "../models";
import { TFunction } from "i18next";

type ConditionMetaData = Partial<{
  [key in ConditionFact]: {
    value: string;
    options: { value: string; label: string; }[];
    operatorOptions: { value: string; label: string; }[];
    thirdOperatorOptions?: { value: string; label: string; }[];
  };
}>;

type OperatorOption = { value: string; label: string };


type ConditionalFactMap = {
  [PageType.PRE_PURCHASE_PRODUCT_PAGE]: ConditionFact.SPECIFIC_PRODUCT | ConditionFact.PRODUCT_TITLE | ConditionFact.PRODUCT_COLLECTION | ConditionFact.PRODUCT_SKU | ConditionFact.PRODUCT_TAGS | ConditionFact.PRODUCT_VARIANT_NAME;
  [PageType.PRE_PURCHASE_ADD_TO_CART_CLICK]: ConditionFact.SPECIFIC_PRODUCT | ConditionFact.PRODUCT_TITLE | ConditionFact.PRODUCT_COLLECTION | ConditionFact.PRODUCT_SKU | ConditionFact.PRODUCT_TAGS | ConditionFact.PRODUCT_VARIANT_NAME;
};


type ConditionalOperatorJson = {
  [P in keyof ConditionalFactMap]: {
    [F in ConditionalFactMap[P]]: OperatorOption[];
  };
};

const conditionalOperatorJson: ConditionalOperatorJson = {
  [PageType.PRE_PURCHASE_PRODUCT_PAGE]: {
    [ConditionFact.SPECIFIC_PRODUCT]: [
      {
        value: "entireProduct",
        label: "The displayed product",
      }
    ],
    [ConditionFact.PRODUCT_TITLE]: [
      {
        value: "entireProduct",
        label: "The title of the displayed product",
      }
    ],
    [ConditionFact.PRODUCT_COLLECTION]: [
      {
        value: "entireProduct",
        label: "The displayed product",
      }
    ],
    [ConditionFact.PRODUCT_SKU]: [
      {
        value: "entireProduct",
        label: "The displayed product SKU",
      }
    ],
    [ConditionFact.PRODUCT_TAGS]: [
      {
        value: "entireProduct",
        label: "The displayed product",
      }
    ],
    [ConditionFact.PRODUCT_VARIANT_NAME]: [
      {
        value: "entireProduct",
        label: "The variant of the displayed product",
      }
    ],
    
  },
  [PageType.PRE_PURCHASE_ADD_TO_CART_CLICK]: {
    [ConditionFact.SPECIFIC_PRODUCT]: [
      {
        value: "productBeingAdded",
        label: "The product being added",
      }
    ],
    [ConditionFact.PRODUCT_TITLE]: [
      {
        value: "productBeingAdded",
        label: "The product being added",
      }
    ],
    [ConditionFact.PRODUCT_COLLECTION]: [
      {
        value: "productBeingAdded",
        label: "The product being added",
      }
    ],
    [ConditionFact.PRODUCT_SKU]: [
      {
        value: "productBeingAdded",
        label: "The product being added",
      }
    ],
    [ConditionFact.PRODUCT_TAGS]: [
      {
        value: "productBeingAdded",
        label: "The product being added",
      }
    ],
    [ConditionFact.PRODUCT_VARIANT_NAME]: [
      {
        value: "productBeingAdded",
        label: "The product being added",
      }
    ],
  }
};

const getConditionsMetaData = (activePage: PageType, isShopifyPlusMerchant: boolean, conditionValue: string, t: TFunction<"translation", undefined>): ConditionMetaData => {
  const getConditionalOptions = (
    conditionFact: ConditionFact
  ): OperatorOption[] => {
    const pageConfig = conditionalOperatorJson[activePage as keyof typeof conditionalOperatorJson];

    if (pageConfig && conditionFact in pageConfig) {

      return pageConfig[conditionFact as keyof typeof pageConfig];
    }

    return [];
  };

  return {
    [ConditionFact.SPECIFIC_PRODUCT]: {
      value: "SPECIFIC_PRODUCT",
      options: [
        ...getConditionalOptions(ConditionFact.SPECIFIC_PRODUCT),
        {
          value: "atLeastOneProduct",
          label: "At least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "Every product in the cart",
        },
      ],
      operatorOptions: [
        {
          value: "is",
          label: "Is from the list",
        },
        {
          value: "isNot",
          label: "Is not from the list",
        },
      ],
    },
    [ConditionFact.PRODUCT_COLLECTION]: {
      value: "PRODUCT_COLLECTION",
      options: [
        ...getConditionalOptions(ConditionFact.PRODUCT_COLLECTION),
        {
          value: "atLeastOneProduct",
          label: "At least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "Every product in the cart",
        },
      ],
      operatorOptions: [
        {
          value: "is",
          label:
            APP_PLATFORM === Platform.Shopify ? "Is in a collection from the list" : "Is in a category from the list",
        },
        {
          value: "isNot",
          label:
            APP_PLATFORM === Platform.Shopify
              ? "Is not in a collection from the list"
              : "Is not in a category from the list",
        },
      ],
    },
    [ConditionFact.PRODUCT_TAGS]: {
      value: "PRODUCT_TAGS",
      options: [
        ...getConditionalOptions(ConditionFact.PRODUCT_TAGS),
        {
          value: "atLeastOneProduct",
          label: "At least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "Every product in the cart",
        },
      ],
      operatorOptions: [
        {
          value: "hasAtLeastOne",
          label: "Has at least one of the tags from the list",
        },
        {
          value: "hasAll",
          label: "Has all the tags from the list",
        },
        {
          value: "notHaveAnyOne",
          label: "Doesn't have some of the tags from the list",
        },
        {
          value: "notHaveAll",
          label: "Doesn't have all the tags from the list",
        },
      ],
    },
    [ConditionFact.PRODUCT_TITLE]: {
      value: "PRODUCT_TITLE",
      options: [
        ...getConditionalOptions(ConditionFact.PRODUCT_TITLE),
        {
          value: "atLeastOneProduct",
          label: "The title of at least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "The title of all products in the cart",
        },
        {
          value: "onlyProduct",
          label: "The only product title in the cart",
        },
      ],
      operatorOptions: getOperatorOptions(ConditionFact.PRODUCT_TITLE, conditionValue, t, t("title")),
    },
    [ConditionFact.DISCOUNT_CODE]: {
      value: "DISCOUNT_CODE",
      options: [],
      operatorOptions: [
        {
          value: "isExactSame",
          label: "Is (exact code)",
        },
        {
          value: "isNotExactSame",
          label: "Is not (exact code)",
        },
        {
          value: "startWith",
          label: "Starts with",
        },
        {
          value: "notStartWith",
          label: "Doesn't start with",
        },
        {
          value: "endWith",
          label: "Ends with",
        },
        {
          value: "notEndWith",
          label: "Doesn't end with",
        },
        {
          value: "contains",
          label: "Contains",
        },
        {
          value: "notContains",
          label: "Doesn't contain",
        },
      ],
    },
    [ConditionFact.PRODUCT_SKU]: {
      value: "PRODUCT_SKU",
      options: [
        ...getConditionalOptions(ConditionFact.PRODUCT_SKU),
        {
          value: "atLeastOneProduct",
          label: "The SKU of at least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "The SKU of all products in the cart",
        },
        {
          value: "onlyProduct",
          label: "The only SKU in the cart",
        },
      ],
      operatorOptions: getOperatorOptions(ConditionFact.PRODUCT_SKU, conditionValue, t, t("SKU")),
    },
    [ConditionFact.CART_OR_ORDER_VALUE]: {
      value: "CART_OR_ORDER_VALUE",
      options: [],
      operatorOptions: [
        {
          value: "eql",
          label: "Equal to",
        },
        {
          value: "notEql",
          label: "Is not equal to",
        },
        {
          value: "grtThan",
          label: "Greater than",
        },
        {
          value: "grtOrEql",
          label: "Greater than or equals to",
        },
        {
          value: "ls",
          label: "Less than",
        },
        {
          value: "lsOrEql",
          label: "Less than or equals to",
        },
        {
          value: "between",
          label: "Between",
        },
        {
          value: "notBetween",
          label: "Is not between",
        },
      ],
    },
    [ConditionFact.PRODUCT_VARIANT_NAME]: {
      value: "PRODUCT_VARIANT_NAME",
      options: [
        ...getConditionalOptions(ConditionFact.PRODUCT_VARIANT_NAME),
        {
          value: "atLeastOneProduct",
          label: "The variant of at least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "The variant of all products in the cart",
        },
        {
          value: "onlyProduct",
          label: "The variant of only product in the cart",
        },
      ],
      operatorOptions: getOperatorOptions(ConditionFact.PRODUCT_VARIANT_NAME, conditionValue, t, t("variant name")),
    },
    [ConditionFact.PRODUCT_SALES_CHANNEL]: {
      value: "PRODUCT_SALES_CHANNEL",
      options: [
        {
          value: "atLeastOneProduct",
          label: "At least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "All products in the cart",
        },
      ],
      operatorOptions: getOperatorOptions(ConditionFact.PRODUCT_SALES_CHANNEL, conditionValue, t)
    },
    [ConditionFact.PRODUCT_PRICE]: {
      value: "PRODUCT_PRICE",
      options: [
        {
          value: "atLeastOneProduct",
          label: "At least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "Every products in the cart",
        },
      ],
      operatorOptions: getOperatorOptions(ConditionFact.PRODUCT_PRICE, conditionValue, t, t("Price")),
    },
    [ConditionFact.CUSTOMER_LANGUAGE]: {
      value: "CUSTOMER_LANGUAGE",
      options: [],
      operatorOptions: [
        {
          value: "containsAtLeastOne",
          label: "Is one of the languages from the list",
        },
        {
          value: "notContainsAtLeastOne",
          label: "Is not one of the languages from the list",
        },
      ],
    },
    [ConditionFact.CUSTOMER_COUNTRY]: {
      value: "CUSTOMER_COUNTRY",
      options: [],
      operatorOptions: [
        {
          value: "containsAtLeastOne",
          label: "Is one of the countries from the list",
        },
        {
          value: "notContainsAtLeastOne",
          label: "Is not one of the countries from the list",
        },
      ],
    },
    [ConditionFact.PREV_PAGE_OFFER]: {
      value: "PREV_PAGE_OFFER",
      options: getPrevPageOfferOptions(activePage),
      operatorOptions: getOperatorOptions(
        ConditionFact.PREV_PAGE_OFFER,
        conditionValue,
        t,
        "",
        activePage,
        isShopifyPlusMerchant
      ),
      ...((activePage === PageType?.THANK_YOU_PAGE || activePage === PageType?.ORDER_STATUS_PAGE || activePage === PageType?.POST_PURCHASE_PAGE1 || activePage === PageType?.POST_PURCHASE_PAGE2) ? {
          thirdOperatorOptions: getThirdOperatorOptions(
            ConditionFact.PREV_PAGE_OFFER)
      } : {})
    },
    [ConditionFact.CUSTOMER_NUMBER_OF_ORDERS]: {
      value: "CUSTOMER_NUMBER_OF_ORDERS",
      options: [],
      operatorOptions: [
        {
          value: "eql",
          label: "Equal to",
        },
        {
          value: "notEql",
          label: "Is not equal to",
        },
        {
          value: "grtThan",
          label: "Greater than",
        },
        {
          value: "grtOrEql",
          label: "Greater than or equals to",
        },
        {
          value: "ls",
          label: "Less than",
        },
        {
          value: "lsOrEql",
          label: "Less than or equals to",
        },
        {
          value: "between",
          label: "Between",
        },
        {
          value: "notBetween",
          label: "Is not between",
        },
      ],
    },
    [ConditionFact.CUSTOMER_EMAIL]: {
      value: "CUSTOMER_EMAIL",
      options: [],
      operatorOptions: [
        {
          value: "isExactSame",
          label: "Is (exact email)",
        },
        {
          value: "isNotExactSame",
          label: "Is not (exact email)",
        },
        {
          value: "startWith",
          label: "Starts with",
        },
        {
          value: "notStartWith",
          label: "Doesn't start with",
        },
        {
          value: "endWith",
          label: "Ends with",
        },
        {
          value: "notEndWith",
          label: "Doesn't end with",
        },
        {
          value: "contains",
          label: "Contains",
        },
        {
          value: "notContains",
          label: "Doesn't contain",
        },
      ],
    },
    [ConditionFact.CUSTOMER_TAG]: {
      value: "CUSTOMER_TAG",
      options:[],
      operatorOptions: [
        {
          value: "hasAtLeastOne",
          label: "customer has at least one of the tags from the list ",
        },
        {
          value: "hasAll",
          label: "customer has all the tags from the list",
        },
        {
          value: "notHaveAnyOne",
          label: "customer doesn't have any of the tags from the list",
        },
        {
          value: "notHaveAll",
          label: "customer doesn't have all the tags from the list",
        },
      ],
    },
    [ConditionFact.CUSTOMER_SEGMENTS]: {
      value: "CUSTOMER_SEGMENTS",
      options:[],
      operatorOptions: [
        {
          value: "containsAtLeastOne",
          label: "Is one of the customer segments from the list",
        },
        {
          value: "notContainsAtLeastOne",
          label: "Is not one of the customer segments from the list",
        },
      ],
    },
    [ConditionFact.MARKETING_CONSENT]: {
      value: "MARKETING_CONSENT",
      options: [],
      operatorOptions: [
        {
          value: "is",
          label: "Customer accepted marketing",
        },
        {
          value: "isNot",
          label: "Customer don't accept marketing",
        },
      ],
    },
    [ConditionFact.PHYSICAL_PRODUCT_OR_REQUIRE_SHIPPING]: {
      value: "PHYSICAL_PRODUCT_OR_REQUIRE_SHIPPING",
      options: [
        {
          value: "atLeastOneProduct",
          label: t("At least one product in the cart"),
        },
        {
          value: "entireOrder",
          label: t("Every product in the cart"),
        },
      ],
      operatorOptions: [
        {
          value: "is",
          label: t("Requires shipping"),
        },
        {
          value: "isNot",
          label: t("Does not require shipping"),
        },
      ],
    },
    [ConditionFact.TOTAL_ITEM_COUNT]: {
      value: "TOTAL_ITEM_COUNT",
      options: [],
      operatorOptions: [
        {
          value: "eql",
          label: "Equals",
        },
        {
          value: "notEql",
          label: "Is not equal to",
        },
        {
          value: "grtThan",
          label: "Is greater than",
        },
        {
          value: "grtOrEql",
          label: "Is greater than or equals to",
        },
        {
          value: "ls",
          label: "Is less than",
        },
        {
          value: "lsOrEql",
          label: "Is less than or equals to",
        },
        {
          value: "between",
          label: "Is between",
        },
        {
          value: "notBetween",
          label: "Is not between",
        },
      ],
    },
    [ConditionFact.SHIPPING_VALUE]: {
      value: "SHIPPING_VALUE",
      options: [],
      operatorOptions: [
        {
          value: "eql",
          label: "Equals",
        },
        {
          value: "notEql",
          label: "Is not equal to",
        },
        {
          value: "grtThan",
          label: "Is greater than",
        },
        {
          value: "grtOrEql",
          label: "Is greater than or equals to",
        },
        {
          value: "ls",
          label: "Is less than",
        },
        {
          value: "lsOrEql",
          label: "Is less than or equals to",
        },
        {
          value: "between",
          label: "Is between",
        },
        {
          value: "notBetween",
          label: "Is not between",
        },
      ],
    },
    [ConditionFact.ORDER_NOTE]: {
      value: "ORDER_NOTE",
      options: [],
      operatorOptions: [
        {
          value: "isExactSame",
          label: "Is (exact code)",
        },
        {
          value: "isNotExactSame",
          label: "Is not (exact code)",
        },
        {
          value: "startWith",
          label: "Starts with",
        },
        {
          value: "notStartWith",
          label: "Doesn't start with",
        },
        {
          value: "endWith",
          label: "Ends with",
        },
        {
          value: "notEndWith",
          label: "Doesn't end with",
        },
        {
          value: "contains",
          label: "Contains",
        },
        {
          value: "notContains",
          label: "Doesn't contain",
        },
      ],
    },
    [ConditionFact.PRODUCT_TYPE]: {
      value: "PRODUCT_TYPE",
      options: [
        {
          value: "atLeastOneProduct",
          label: "At least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "All products in the cart",
        },
      ],
      operatorOptions: [
        {
          value: "is",
          label: "Is of a type from the list",
        },
        {
          value: "isNot",
          label: "Is not of a type from the list",
        }
      ],
    },
    [ConditionFact.FULFILLMENT_STATUS]: {
      value: "FULFILLMENT_STATUS",
      options:[],
      operatorOptions: [
        {
          value: "containsAtLeastOne",
          label: "Is one of the fulfillment statuses in the list",
        },
        {
          value: "notContainsAtLeastOne",
          label: "Is not one of the fulfillment statuses in the list",
        }
      ],
    },
    [ConditionFact.PAYMENT_STATUS]: {
      value: "PAYMENT_STATUS",
      options: [],
      operatorOptions: [
        {
          value: "containsAtLeastOne",
          label: "Is one of the payment status from the list",
        },
        {
          value: "notContainsAtLeastOne",
          label: "Is not one of the payment statuses in the list",
        },
      ],
    },
    [ConditionFact.PRODUCT_VENDOR]: {
      value: "PRODUCT_VENDOR",
      options: [
        {
          value: "atLeastOneProduct",
          label: "At least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "Every product in the cart",
        },
      ],
      operatorOptions: [
        {
          value: "is",
          label: "Is a vendor in the list",
        },
        {
          value: "isNot",
          label: "Is not a vendor in the list",
        },
      ],
    },
    [ConditionFact.SHIPPING_METHOD]: {
      value: "SHIPPING_METHOD",
      options: [],
      operatorOptions: [
        {
          value: "containsAtLeastOne",
          label: "Is one of the methods in the list",
        },
        {
          value: "notContainsAtLeastOne",
          label: "Is not one of the methods in the list",
        },
      ],
    },
    [ConditionFact.ORDER_TAGS]: {
      value: "ORDER_TAGS",
      options: [],
      operatorOptions: [
        {
          value: "hasAtLeastOne",
          label: t("Is tagged with at least one of the tags in the list"),
        },
        {
          value: "hasAll",
          label: t("Is tagged with all the tags in the list"),
        },
        {
          value: "notHaveAll",
          label: t("Is not tagged with any of the tags in the list"),
        },
      ]
    },
    [ConditionFact.PRODUCT_ITEM_COST]: {
      value: "PRODUCT_ITEM_COST",
      options: [
        {
          value: "atLeastOneProduct",
          label: "At least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "Every products in the cart",
        },
      ],
      operatorOptions: getOperatorOptions(ConditionFact.PRODUCT_ITEM_COST, conditionValue, t, t("Item cost")),
    },
    [ConditionFact.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL]: {
      value: "PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL",
      options: [
        {
          value: "atLeastOneProduct",
          label: "At least one product in the cart",
        },
        {
          value: "entireOrder",
          label: "Every products in the cart",
        },
      ],
      operatorOptions: getOperatorOptions(ConditionFact.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL, conditionValue, t)
    },
    [ConditionFact.PAYMENT_METHOD]: {
      value: "PAYMENT_METHOD",
      options: [],
      operatorOptions: [
        {
          value: "containsAtLeastOne",
          label: "Is one of the payment in the list",
        },
        {
          value: "notContainsAtLeastOne",
          label: "Is not one of the payment in the list",
        },
      ],
    },
    [ConditionFact.CUSTOMER_NUMBER_OF_ORDERS]: {
      value: "CUSTOMER_NUMBER_OF_ORDERS",
      options: [],
      operatorOptions: [
        {
          value: "eql",
          label: "Equals",
        },
        {
          value: "notEql",
          label: "Is not equal to",
        },
        {
          value: "grtThan",
          label: "Is greater than",
        },
        {
          value: "ls",
          label: "Is less than",
        },
        {
          value: "between",
          label: "Is between",
        },
        {
          value: "notBetween",
          label: "Is not between",
        },
      ],
    },
    [ConditionFact.CUSTOMER_EMAIL]: {
      value: "CUSTOMER_EMAIL",
      options: [],
      operatorOptions: [
        {
          value: "isExactSame",
          label: "Is",
        },
        {
          value: "isNotExactSame",
          label: "Is not",
        },
        {
          value: "startWith",
          label: "Starts with",
        },
        {
          value: "notStartWith",
          label: "Doesn't start with",
        },
        {
          value: "endWith",
          label: "Ends with",
        },
        {
          value: "notEndWith",
          label: "Doesn't end with",
        },
        {
          value: "contains",
          label: "Contains",
        },
        {
          value: "notContains",
          label: "Doesn't contain",
        },
      ],
    },
    [ConditionFact.CUSTOMER_TIME_SINCE_LAST_ORDER]: {
      value: "CUSTOMER_TIME_SINCE_LAST_ORDER",
      options: [
        {
          value: "grtThan",
          label: "Is greater than",
        },
        {
          value: "ls",
          label: "Is less than",
        },
        {
          value: "between",
          label: "Is between",
        },
        {
          value: "notBetween",
          label: "Is not between",
        },
      ],
      operatorOptions: [
        {
          value: "MINUTES",
          label: "Minutes",
        },
        {
          value: "HOURS",
          label: "Hours",
        },
        {
          value: "DAYS",
          label: "Days",
        },
        {
          value: "WEEKS",
          label: "Weeks",
        },
        {
          value: "MONTHS",
          label: "Months",
        },
        {
          value: "YEARS",
          label: "Years",
        },
      ],
    },
    [ConditionFact.CUSTOMER_TAGS]: {
      value: "CUSTOMER_TAGS",
      options: [],
      operatorOptions: [
        {
          value: "hasAtLeastOne",
          label: t("Is tagged with at least one of the tags in the list"),
        },
        {
          value: "hasAll",
          label: t("Is tagged with all the tags in the list"),
        },
        {
          value: "notHaveAtLeastOne",
          label: t("Is not tagged with at least one of the tags in the list"),
        },
        {
          value: "notHaveAll",
          label: t("Is not tagged with all the tags in the list"),
        },
      ],
    },
    [ConditionFact.CUSTOMER_BIRTHDAY]: {
      value: "CUSTOMER_BIRTHDAY",
      options: [
        {
          value: "on",
          label: t("Is on"),
        },
        {
          value: "notOn",
          label: t("Is not on"),
        },
        {
          value: "before",
          label: t("Is before"),
        },
        {
          value: "after",
          label: t("Is after"),
        },
        {
          value: "between",
          label: t("Between"),
        },
        {
          value: "notBetween",
          label: t("Not between"),
        },
        {
          value: "lessThan",
          label: t("in less than"),
        },
        {
          value: "elapsed",
          label: t("Passed X time ago"),
        }
      ],
      operatorOptions: getOperatorOptions(ConditionFact.CUSTOMER_BIRTHDAY, conditionValue, t),
    },
  }
};

const getPrevPageOfferOptions = (activePage?: PageType) => {
  if (activePage === PageType?.POST_PURCHASE_PAGE1) {
    return [
      {
        value: "accepted",
        label: "An offer was accepted",
      },
    ];
  } else {
    return [
      {
        value: "accepted",
        label: "An offer was accepted",
      },
      // {
      //   value: "noOfferAccepted",
      //   label: "No offer was accepted",
      // },
      {
        value: "declined",
        label: "An offer was declined",
      },
    ];
  }
};

const getOperatorOptions = (
  conditionName: string,
  conditionValue: string,
  t: TFunction<"translation", undefined>,
  name?: string,
  activePage?: PageType,
  isShopifyPlusMerchant?: boolean
) => {
  switch (conditionName) {
    case ConditionFact.PRODUCT_TITLE:
    case ConditionFact.PRODUCT_SKU:
    case ConditionFact.PRODUCT_VARIANT_NAME:
      const commonOptions = [
        {
          value: "startWith",
          label: "Starts with",
        },
        {
          value: "notStartWith",
          label: "Doesn't start with",
        },
        {
          value: "endWith",
          label: "Ends with",
        },
        {
          value: "notEndWith",
          label: "Doesn't end with",
        },
        {
          value: "contains",
          label: "Contains",
        },
        {
          value: "notContains",
          label: "Doesn't contain",
        },
      ];

      const exactSameOrNotOptions = [
        {
          value: "isExactSame",
          label: `${t("Is (exact")} ${name})`,
        },
        {
          value: "isNotExactSame",
          label: `${t("Is not (exact")} ${name})`,
        },
      ];

      switch (conditionValue) {
        case "atLeastOneProduct":
          return [...exactSameOrNotOptions, ...commonOptions];

        case "entireOrder":
        case "entireProduct":
        case "productBeingAdded":
          return [...commonOptions];

        case "onlyProduct":
          return [...exactSameOrNotOptions];

        default:
          return [];
      }

    case ConditionFact.PRODUCT_SALES_CHANNEL:
      switch (conditionValue) {
        case "atLeastOneProduct":
          return [
            {
              value: "isAvailableAtOneOfTheSalesChannelsFromTheList",
              label: "Is available at one of the sales channels from the list",
            },
            {
              value: "isAvailableAtAllTheSalesChannelsFromTheList",
              label: "Is available at all the sales channels from the list",
            },
            {
              value: "isNotAvailableAtOneOfTheSalesChannelsFromTheList",
              label: "Is not available at one of the sales channels from the list",
            },
            {
              value: "isNotAvailableAtAllTheSalesChannelsFromTheList",
              label: "Is not available at all the sales channels from the list",
            },
          ];

        case "entireOrder":
          return [
            {
              value: "areAvailableAtOneOfTheSalesChannelsFromTheList",
              label: "Are available at one of the sales channels from the list",
            },
            {
              value: "areAvailableAtAllTheSalesChannelsFromTheList",
              label: "Are available at all the sales channels from the list",
            },
            {
              value: "areNotAvailableAtOneOfTheSalesChannelsFromTheList",
              label: "Are not available at one of the sales channels from the list",
            },
            {
              value: "areNotAvailableAtAllTheSalesChannelsFromTheList",
              label: "Are not available at all the sales channels from the list",
            },
          ];
        default:
          return [];
      }

    case ConditionFact.PRODUCT_PRICE:
    case ConditionFact.PRODUCT_ITEM_COST:
      switch (conditionValue) {
        case "atLeastOneProduct":
        case "entireOrder":
          return [
            {
              value: "eql",
              label: "Equals",
            },
            {
              value: "notEql",
              label: "Is not equal to",
            },
            {
              value: "grtThan",
              label: "Is greater than",
            },
            {
              value: "grtOrEql",
              label: "Is greater than or equals to",
            },
            {
              value: "ls",
              label: "Is less than",
            },
            {
              value: "lsOrEql",
              label: "Is less than or equals to",
            },
            {
              value: "between",
              label: "Is between",
            },
            {
              value: "notBetween",
              label: "Is not between",
            },
          ];
        default:
          return [];
      }
    
    case ConditionFact.PREV_PAGE_OFFER:
      const operators = [];

      if (isShopifyPlusMerchant && conditionValue === "accepted") {
        operators.push({
          value: PageType?.CHECKOUT,
          label: "On the checkout",
        });
      }

      if (
        activePage === PageType?.POST_PURCHASE_PAGE2 ||
        activePage === PageType?.THANK_YOU_PAGE ||
        activePage === PageType?.ORDER_STATUS_PAGE
      ) {
        operators.push({
          value: PageType?.POST_PURCHASE_PAGE1,
          label: "On the post purchase 1st page",
        });
      }

      if (activePage === PageType?.THANK_YOU_PAGE || activePage === PageType?.ORDER_STATUS_PAGE) {
        operators.push({
          value: PageType?.POST_PURCHASE_PAGE2,
          label: "On the post purchase 2nd page",
        });
        operators.push({
          value: `${PageType?.POST_PURCHASE_PAGE1}-${PageType?.POST_PURCHASE_PAGE2}`,
          label: "On one of the post purchase pages",
        });
        operators.push({
          value: `${PageType?.POST_PURCHASE_PAGE1}&${PageType?.POST_PURCHASE_PAGE2}`,
          label: "On all of the post purchase pages",
        });
      }

      if (activePage === PageType?.ORDER_STATUS_PAGE && conditionValue === "accepted") {
        operators.push({
          value: PageType?.THANK_YOU_PAGE,
          label: "On the thank you page",
        });
      }

      return operators;

    case ConditionFact.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL:
      switch (conditionValue) {
        case "atLeastOneProduct":
        case "entireOrder":
          return [
            {
              value: "isAvailableAtOneOfTheSalesChannelsFromTheList",
              label: "Is available at one of the sales channels in the list",
            },
            {
              value: "isNotAvailableAtOneOfTheSalesChannelsFromTheList",
              label: "Is not available at one of the sales channels in the list",
            },
            {
              value: "isAvailableAtAllTheSalesChannelsFromTheList",
              label: "Is available at all the sales channels in the list",
            },
            {
              value: "isNotAvailableAtAllTheSalesChannelsFromTheList",
              label: "Is not available at all the sales channels in the list",
            },
          ];
        default:
          return [];
      }
    
    case ConditionFact.CUSTOMER_BIRTHDAY:
      switch (conditionValue) {
        case "lessThan":
        case "elapsed":
          return [
          {
            value: "DAYS",
            label: "Days",
          },
          {
            value: "WEEKS",
            label: "Weeks",
          },
          {
            value: "MONTHS",
            label: "Months",
          },
        ];
        default:
          return [];
      }

    default:
      return [];
  }
};

const getThirdOperatorOptions = (conditionName: String) => {
  switch (conditionName) {
    case ConditionFact.PREV_PAGE_OFFER:

      return [{
        value: 'ANY',
        label: "On any condition",
      }, {
        value: 'ONEOFSELECTED',
        label: "On one of the selected condition"
      }];

    default:
      return []
  }
}

export { getConditionsMetaData };
